import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import { extractMentions, renderTextWithLinks } from '../utils/text';
import { createNotification } from '../utils/notificationHelpers';
import { createComment, deleteComment, likeComment, listComments, unlikeComment, updateComment } from '../utils/secureFunctions';
import ImageWithFallback from './ImageWithFallback';
import UserLink from './UserLink';
import VerifiedBadge from './VerifiedBadge';
import { appConfirm } from '../utils/dialog';

type ContentType = 'post' | 'pulse' | 'testimony';

interface CommentDoc {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  content: string;
  likeCount: number;
  likedByMe: boolean;
  parentId?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface CommentsInlineProps {
  contentId: string;
  contentType: ContentType;
  contentOwnerId?: string;
  className?: string;
  style?: React.CSSProperties;
}

const CommentsInline: React.FC<CommentsInlineProps> = ({ contentId, contentType, contentOwnerId, className, style }) => {
  const [comments, setComments] = useState<CommentDoc[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState('');

  useEffect(() => {
    const u = auth.currentUser;
    setUserName(u?.displayName || u?.email?.split('@')[0] || 'Anonymous');
    setUserPhoto(u?.photoURL || '');
  }, []);

  const refresh = async () => {
    try {
      const res = await listComments({ contentType, contentId, limit: 500 });
      setComments(res.comments as any);
    } catch (e) {
      console.error('Failed to load comments', e);
    }
  };

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, contentType]);

  const roots = useMemo(() => comments.filter((c) => !c.parentId), [comments]);
  const repliesByParent = useMemo(() => {
    const map = new Map<string, CommentDoc[]>();
    for (const c of comments) {
      if (c.parentId) {
        if (!map.has(c.parentId)) map.set(c.parentId, []);
        map.get(c.parentId)!.push(c);
      }
    }
    return map;
  }, [comments]);

  const postComment = async (text: string, parentId?: string | null) => {
    if (!auth.currentUser) return;
    const v = validateContent(text);
    if (!v.isValid) {
      alert(v.message);
      return;
    }
    const safe = filterProfanity(text.trim());

    await createComment({
      contentType,
      contentId,
      content: safe,
      parentId: parentId || null,
      userName,
      userPhoto,
    });
    await refresh();

    if (contentOwnerId && contentOwnerId !== auth.currentUser.uid) {
      await createNotification(
        contentOwnerId,
        'comment',
        auth.currentUser.uid,
        userName || 'Someone',
        userPhoto || '',
        `${userName || 'Someone'} commented on your ${contentType}`,
        contentId,
        contentType,
        { snippet: safe.slice(0, 140) }
      );
    }

    try {
      const mentions = extractMentions(safe);
      // Mention-to-uid lookup previously relied on Firestore; skip for now.
      if (mentions.length) {
        // no-op
      }
    } catch (e) {
      console.warn('Mention notifications failed', e);
    }
  };

  const handleAdd = async (parentId?: string) => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await postComment(newComment, parentId);
      setNewComment('');
      setReplyingTo(null);
    } catch (e) {
      console.error(e);
      alert('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!auth.currentUser) return;
    const c = comments.find((x) => x.id === commentId);
    const liked = !!c?.likedByMe;
    if (liked) {
      await unlikeComment(commentId);
    } else {
      await likeComment(commentId);
    }
    await refresh();
  };

  const handleEdit = async (commentId: string) => {
    const text = editContent.trim();
    if (!text) return;
    const v = validateContent(text);
    if (!v.isValid) {
      alert(v.message);
      return;
    }
    const safe = filterProfanity(text);
    await updateComment(commentId, { content: safe });
    setEditingId(null);
    setEditContent('');
    await refresh();
  };

  const handleDelete = async (commentId: string) => {
    if (!await appConfirm('Delete this comment?')) return;
    try {
      await deleteComment(commentId);
      await refresh();
    } catch (e) {
      console.error('Delete comment failed', e);
      alert('Failed to delete comment');
    }
  };

  const CommentItem: React.FC<{ c: CommentDoc; isReply?: boolean }> = ({ c, isReply }) => (
    <div key={c.id} style={{ ...styles.comment, marginLeft: isReply ? 40 : 0 }}>
      <ImageWithFallback src={c.userPhoto} alt="" style={styles.avatar} />
      <div style={styles.commentContent}>
        <UserLink as="strong" userId={c.userId} style={styles.commentUser}>
          {c.userName}
          {(() => {
            const explicit = typeof c.userVerified === 'boolean' ? c.userVerified : undefined;
            const flag = typeof explicit === 'boolean' ? explicit : false;
            return flag ? <VerifiedBadge size={12} /> : null;
          })()}
        </UserLink>

        {editingId === c.id ? (
          <div style={{ marginTop: 6 }}>
            <input
              type="text"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={styles.input}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => handleEdit(c.id)} style={styles.smallButton}>Save</button>
              <button onClick={() => { setEditingId(null); setEditContent(''); }} style={styles.smallButtonSecondary}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p style={styles.commentText}>{renderTextWithLinks(c.content)}</p>
            {c.updatedAt && <span style={styles.editedLabel}>(edited)</span>}
          </>
        )}

        <div style={styles.commentActions}>
          <span style={styles.commentTime}>{c.createdAt ? new Date(c.createdAt).toLocaleString() : 'Just now'}</span>
          <button onClick={() => handleLike(c.id)} style={styles.commentActionButton}>
            {c.likedByMe ? '❤️' : '🤍'} {c.likeCount}
          </button>
          {!isReply && (
            <button onClick={() => setReplyingTo(c.id)} style={styles.commentActionButton}>Reply</button>
          )}
          {c.userId === auth.currentUser?.uid && (
            <>
              <button onClick={() => { setEditingId(c.id); setEditContent(c.content); }} style={styles.commentActionButton}>Edit</button>
              <button onClick={() => handleDelete(c.id)} style={styles.commentActionButton}>Delete</button>
            </>
          )}
        </div>

        {replyingTo === c.id && (
          <div style={{ marginTop: 8 }}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd(c.id)}
              placeholder={`Reply to ${c.userName}...`}
              style={styles.input}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => handleAdd(c.id)} disabled={posting} style={styles.smallButton}>{posting ? '...' : 'Reply'}</button>
              <button onClick={() => { setReplyingTo(null); setNewComment(''); }} style={styles.smallButtonSecondary}>Cancel</button>
            </div>
          </div>
        )}

        {repliesByParent.get(c.id)?.map((r) => (
          <CommentItem key={r.id} c={r} isReply />
        ))}
      </div>
    </div>
  );

  return (
    <div className={className} style={{ ...styles.wrapper, ...(style || {}) }}>
      <div style={styles.headerRow}>
        <h4 style={styles.inlineTitle}>Comments</h4>
      </div>

      <div style={styles.commentsList}>
        {roots.length === 0 ? (
          <p style={styles.emptyText}>No comments yet. Be the first!</p>
        ) : (
          roots.map((c) => <CommentItem key={c.id} c={c} />)
        )}
      </div>

      <div style={styles.inputSection}>
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !replyingTo && handleAdd()}
          placeholder="Write a comment..."
          style={styles.input}
        />
        <button onClick={() => handleAdd()} disabled={posting || !newComment.trim()} style={styles.postButton}>
          {posting ? '...' : 'Post'}
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    marginTop: 10,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border)',
  },
  inlineTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' },
  commentsList: { maxHeight: 320, overflowY: 'auto', padding: '10px 12px' },
  emptyText: { textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: 13, padding: '6px 0' },
  comment: { display: 'flex', gap: 10, marginBottom: 12 },
  avatar: { width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  commentContent: { flex: 1 },
  commentUser: { fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 4 },
  commentText: { fontSize: 13, color: 'var(--color-text-primary)', margin: '4px 0', lineHeight: 1.4 },
  editedLabel: { fontSize: 11, color: '#999', marginLeft: 6 },
  commentActions: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 },
  commentTime: { fontSize: 11, color: 'var(--color-text-secondary)' },
  commentActionButton: { background: 'none', border: 'none', color: '#0B6B6F', fontSize: 12, cursor: 'pointer', padding: '2px 6px' },
  inputSection: { display: 'flex', gap: 10, padding: '10px 12px', borderTop: '1px solid var(--color-border)' },
  input: { flex: 1, padding: '10px', fontSize: 14, borderRadius: 20, border: '1px solid #E0E0E0', outline: 'none' },
  postButton: { padding: '8px 14px', backgroundColor: '#17A2A9', color: '#FFFFFF', border: 'none', borderRadius: 18, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  smallButton: { padding: '4px 10px', backgroundColor: '#17A2A9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  smallButtonSecondary: { padding: '4px 10px', backgroundColor: '#E0E0E0', color: '#072B2C', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
};

export default CommentsInline;



